// The floor plan's undo/redo machinery: snapshot-based history — the whole
// layout (walls + placements + measurements) is serialized to a JSON string
// after each committed change; undo/redo just restore a snapshot. Owned here
// (module scope) so any floor slice can import commitFloor directly; the
// serialize/restore functions stay with floor-mode (the restore path re-bakes
// placements from the model cache) and are injected once at install.

export interface FloorHistoryDeps {
  snapshot: () => string; // serialize the current layout
  restore: (json: string) => void; // rebuild the layout from a snapshot
  onChange: () => void; // refresh the sidebar + save-dirty UI
}

const FLOOR_HISTORY_MAX = 200;
const floorUndo: string[] = [];
const floorRedo: string[] = [];
let floorCurrentJson = "";
// The committed snapshot string at the last save / load / blank — the layout is
// "dirty" when floorCurrentJson differs from it. We compare the canonical stored
// snapshot strings (not a freshly recomputed snapshot()) so that undoing back
// to the saved state reads as clean even if a serialize→reload round-trip isn't
// byte-identical. markFloorClean() flushes any pending edit first so the
// baseline matches exactly what was saved (it must NOT reset undo history).
let floorSavedJson = "";

let deps: FloorHistoryDeps | null = null;
export function initFloorHistory(d: FloorHistoryDeps): void {
  deps = d;
}

// "Maybe dirty" gate for commitFloor: the wall/placement/measure systems (and
// the direct-transform drag release paths) mark this whenever they mutate
// serialize-visible state, so a commitFloor with no mark since the last one
// can return in O(1) instead of serializing the whole layout just to discover
// nothing changed. Over-marking is free (the string compare below is the
// backstop); a MISSED mark only folds that edit into the next marked commit's
// undo step — never data loss. Starts true so the first commit always runs.
let floorMaybeDirty = true;
export function markFloorMutation(): void {
  floorMaybeDirty = true;
}

// Capture the current layout as the new baseline; clears the redo branch.
// Called after each committed change (discrete action or finished drag).
// No-ops if nothing actually changed, so it's safe to call liberally (e.g. on
// any release).
export function commitFloor(): void {
  if (!deps || !floorMaybeDirty) return;
  const snap = deps.snapshot();
  floorMaybeDirty = false;
  if (snap === floorCurrentJson) return;
  floorUndo.push(floorCurrentJson);
  if (floorUndo.length > FLOOR_HISTORY_MAX) floorUndo.shift();
  floorRedo.length = 0;
  floorCurrentJson = snap;
  deps.onChange(); // an edit may have made the layout dirty
}

// Undo/redo fold any uncommitted mutation into its own undo step first
// (commitFloor is O(1) when nothing is marked). This keeps the scene exactly
// equal to floorCurrentJson when restore() runs — which the section-diffing
// restore relies on — and means Ctrl+Z after a stray uncommitted edit undoes
// THAT edit instead of silently wiping it while jumping a step further back.
export function floorUndoAction(): void {
  if (!deps) return;
  commitFloor();
  if (floorUndo.length === 0) return;
  floorRedo.push(floorCurrentJson);
  const json = floorUndo.pop()!;
  deps.restore(json);
  floorCurrentJson = json;
  floorMaybeDirty = false; // restore left the scene == floorCurrentJson
  deps.onChange(); // undo/redo can cross the saved baseline
}
export function floorRedoAction(): void {
  if (!deps) return;
  commitFloor(); // an uncommitted edit clears the redo branch, as any edit does
  if (floorRedo.length === 0) return;
  floorUndo.push(floorCurrentJson);
  const json = floorRedo.pop()!;
  deps.restore(json);
  floorCurrentJson = json;
  floorMaybeDirty = false;
  deps.onChange();
}

// Reset the baseline to the current state (app start, plan load).
export function resetFloorHistory(): void {
  if (!deps) return;
  floorUndo.length = 0;
  floorRedo.length = 0;
  floorCurrentJson = deps.snapshot();
  floorMaybeDirty = false; // the snapshot IS the live scene
  floorSavedJson = floorCurrentJson; // a freshly loaded/blank layout is the clean baseline
  deps.onChange();
}

export function markFloorClean(): void {
  commitFloor(); // fold any uncommitted edit into floorCurrentJson
  floorSavedJson = floorCurrentJson;
}
export function floorDirty(): boolean {
  return floorCurrentJson !== floorSavedJson;
}
